import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Close from '@mui/icons-material/Close'
import Database from '../../types/Database'
import { getNavigationTarget } from '../../utils/notificationNavigation'

export interface NotificationItemProps {
  notification: Database.NotificationDocument
  onMarkRead: (id: string) => void
  onDismiss: (id: string) => void
  onNavigate: (path: string) => void
}

function getRelativeTime(timestamp: { toDate: () => Date }): string {
  const now = Date.now()
  const then = timestamp.toDate().getTime()
  const diffMs = now - then

  const minutes = Math.floor(diffMs / 60_000)
  const hours = Math.floor(diffMs / 3_600_000)
  const days = Math.floor(diffMs / 86_400_000)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  return `${days} day${days === 1 ? '' : 's'} ago`
}

const NotificationItem = ({
  notification,
  onMarkRead,
  onDismiss,
  onNavigate
}: NotificationItemProps) => {
  const handleClick = () => {
    onMarkRead(notification.id)
    onNavigate(getNavigationTarget(notification.type, notification.metadata))
  }

  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDismiss(notification.id)
  }

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <IconButton
          edge='end'
          aria-label='dismiss notification'
          onClick={handleDismiss}
          size='small'
        >
          <Close fontSize='small' />
        </IconButton>
      }
    >
      <ListItemButton
        onClick={handleClick}
        sx={{
          backgroundColor: notification.read ? 'transparent' : 'action.hover',
          '&:hover': {
            backgroundColor: notification.read
              ? 'action.hover'
              : 'action.selected'
          }
        }}
      >
        <ListItemText
          primary={
            <Typography
              variant='body1'
              component='span'
              sx={{ fontWeight: notification.read ? 400 : 700 }}
            >
              {notification.title}
            </Typography>
          }
          secondary={
            <>
              <Typography
                variant='body2'
                component='span'
                color='text.secondary'
                sx={{ display: 'block' }}
              >
                {notification.body}
              </Typography>
              <Typography
                variant='caption'
                component='span'
                color='text.secondary'
              >
                {getRelativeTime(notification.createdAt)}
              </Typography>
            </>
          }
        />
      </ListItemButton>
    </ListItem>
  )
}

export default NotificationItem
